"use client";

import { c as _c } from "react/compiler-runtime";
import type { StaticImageData } from "next/image";
import { useId, useState } from "react";
import { IconBox } from "@/components/icon-box";
import { cn } from "@/lib/utils";
import IconConstruir from "~/public/assets/flowfoundation/icons/icon-construir.svg";
import IconDefinir from "~/public/assets/flowfoundation/icons/icon-definir.svg";
import IconEntender from "~/public/assets/flowfoundation/icons/icon-entender.svg";
import IconEntregar from "~/public/assets/flowfoundation/icons/icon-entregar.svg";
import IconEstruturar from "~/public/assets/flowfoundation/icons/icon-estruturar.svg";
import IconRefinar from "~/public/assets/flowfoundation/icons/icon-refinar.svg";
import { BulletList } from "../_components/bullet-list";
import { entregavelTermos } from "../_content/entregaveis";

/**
 * Seção "como-funciona" — o processo das duas semanas.
 * Toggle Semana 1 / Semana 2: troca a descrição e as etapas.
 * Esquerda: chamada + toggle + semana ativa. Direita: cards das etapas.
 */

type Semana = {
  label: string;
  description: string;
  etapas: {
    icon: StaticImageData;
    title: string;
    description: string;
    items?: string[];
  }[];
};
const semanas: Semana[] = [{
  label: "Semana 1",
  description: "Descobrimos o problema, definimos a estratégia e construímos a base do produto.",
  etapas: [{
    icon: IconEntender,
    title: "Entender",
    description: "Compreendemos o negócio, os objetivos, os usuários e os desafios que o produto precisa resolver."
  }, {
    icon: IconDefinir,
    title: "Definir",
    description: "Transformamos as ideias em fluxos, jornadas e wireframes que orientam a construção da solução."
  }, {
    icon: IconEstruturar,
    title: "Estruturar",
    description: "Definimos a arquitetura inicial, a stack tecnológica e a estratégia técnica para iniciar o desenvolvimento com segurança."
  }]
}, {
  label: "Semana 2",
  description: "Transformamos a estratégia em uma primeira versão funcional e um plano claro para evolução do produto.",
  etapas: [{
    icon: IconConstruir,
    title: "Construir",
    description: "Desenvolvemos o frontend das funcionalidades principais em código, utilizando desenvolvimento acelerado por IA + expertise dos nossos especialistas da Flowlab."
  }, {
    icon: IconRefinar,
    title: "Refinar",
    description: "Validamos a solução construída, refinamos a experiência, priorizamos o roadmap e definimos os próximos passos do desenvolvimento."
  }, {
    icon: IconEntregar,
    title: "Entregar",
    description: "Entregamos toda a documentação e os ativos necessários para evoluir o produto com segurança:",
    items: [entregavelTermos.discovery, "Frontend funcional em código", entregavelTermos.arquitetura, entregavelTermos.roadmap, entregavelTermos.estimativa]
  }]
}];
export function FlowFoundationComoFunciona() {
  const $ = _c(21);
  const [ativa, setAtiva] = useState(0);
  const semana = semanas[ativa];
  const baseId = useId();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="tag">Como funciona</p>;
    t1 = <h2 className="title max-w-sm">Duas semanas para estruturar seu produto.</h2>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== ativa || $[3] !== baseId) {
    t2 = semanas.map((item, index) => <button key={item.label} type="button" role="tab" id={`${baseId}-tab-${index}`} aria-selected={ativa === index} aria-controls={`${baseId}-panel-${index}`} onClick={() => setAtiva(index)} className={cn("cursor-pointer rounded-xl px-5 py-3 text-sm leading-[140%] transition-colors", ativa === index ? "bg-primary-300 text-white" : "text-gray-100/75 hover:text-white")}>{item.label}</button>);
    $[2] = ativa;
    $[3] = baseId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <div role="tablist" aria-label="Semanas do programa" className="mt-6 inline-flex w-fit gap-1 rounded-2xl border border-gray-300 p-1.5">{t2}</div>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== semana.description) {
    t4 = <p className="description mt-10 max-w-sm">{semana.description}</p>;
    $[7] = semana.description;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div>{t0}{t1}{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = `${baseId}-panel-${ativa}`;
  const t7 = `${baseId}-tab-${ativa}`;
  let t8;
  if ($[12] !== semana.etapas) {
    t8 = semana.etapas.map(_temp);
    $[12] = semana.etapas;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t7 || $[16] !== t8) {
    t9 = <div role="tabpanel" id={t6} aria-labelledby={t7} className="flex flex-col gap-2">{t8}</div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t5 || $[19] !== t9) {
    t10 = <section className="md:container-padding relative bg-gray-1100 border-t border-gray-300 text-white" id="flowfoundation-como-funciona"><div className="container relative md:border-x md:border-gray-300 px-6 lg:px-18 3xl:px-24! py-20 md:py-28"><div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_1.5fr] lg:gap-16">{t5}{t9}</div></div></section>;
    $[18] = t5;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
function _temp(etapa) {
  return <div key={etapa.title} className="flex gap-6 rounded-2xl bg-gray-1000 p-6 md:gap-9 md:p-12"><IconBox bordered={true} icon={etapa.icon} className="max-md:size-16" /><div className="flex flex-col gap-6"><h3 className="text-xl font-medium tracking-[-0.02em]">{etapa.title}</h3><p className="text-base leading-[1.6] text-gray-100/75">{etapa.description}</p>{etapa.items && <BulletList items={etapa.items} className="gap-3" />}</div></div>;
}